import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { bzIconCheckCircle, bzIconClose, bzIconError, bzIconInfo } from '@bringoz/ui/icons';
import { BzIcon } from '../icon/bz-icon';
import type { BzIconDef } from '../icon/bz-icon-def';

/** What happened. `default` is the neutral acknowledgement, not an absence of tone. */
export type BzToastTone = 'default' | 'positive' | 'negative';

/**
 * `card` carries a title and actions; `push` is the single-line strip with no
 * room for either.
 */
export type BzToastVariant = 'card' | 'push';

const TONE_ICONS: Record<BzToastTone, BzIconDef> = {
  default: bzIconInfo,
  positive: bzIconCheckCircle,
  negative: bzIconError,
};

/**
 * A transient notification.
 *
 * Named `toast` rather than after the design's own label: Figma calls this
 * family `Dialog-*` / `Push-*`, but `bz-dialog-*` in this library is already
 * the modal that takes over the screen, and two things called dialog is how a
 * call site ends up importing the wrong one.
 *
 * Presentational only — it renders one notification and reports that the
 * viewer dismissed it. Stacking, placement and auto-dismiss belong to the
 * service that will own a queue of these, the same split `bz-dialog-layout`
 * and `BzDialogService` already use.
 *
 * Actions are projected rather than inputs, because every tone in the design
 * pairs a different set: positive has one solid button, negative a text
 * button beside a solid one, default a text button beside an outline. Taking
 * them as data would mean re-describing `bz-button` through this component's
 * API.
 *
 * Two values in the design have no token to land on and take the nearest one
 * instead, rather than smuggling in a literal: the 10px corner radius
 * (`--bz-radius-lg` is 8px) and the 16px status icon (the icon scale steps
 * 14px → 20px). The positive tone's green is a third: the design's `#42a942`
 * is not `--bz-color-success`, and that difference belongs to the palette
 * migration that has not been taken yet.
 */
@Component({
  selector: 'bz-toast',
  imports: [BzIcon, NgTemplateOutlet],
  templateUrl: './bz-toast.html',
  styleUrl: './bz-toast.scss',
  host: {
    '[class]': 'hostClass()',
    // role carries its own politeness, so aria-live alongside it would only be
    // a second, contradictory answer: status is polite, alert is assertive.
    '[attr.role]': 'tone() === "negative" ? "alert" : "status"',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzToast {
  readonly tone = input<BzToastTone>('default');
  readonly variant = input<BzToastVariant>('card');

  /** Card only. A push has no header to put it in. */
  readonly title = input('');

  readonly dismissible = input(true);

  /**
   * Accessible name for the dismiss control. It is an icon-only button, so
   * without this there is nothing for a screen reader to announce.
   */
  readonly dismissLabel = input('Dismiss');

  readonly dismissed = output<void>();

  protected readonly closeIcon = bzIconClose;
  protected readonly toneIcon = computed(() => TONE_ICONS[this.tone()]);
  protected readonly hostClass = computed(
    () => `bz-toast bz-toast--${this.variant()} bz-toast--${this.tone()}`,
  );
}
